// ChiDesk faceted spa booking panel — ON-SELECT, LIVE, therapist-last.
//
// The menu groups treatments by TREATMENT TYPE — the ChiDesk Category
// ("Massage Therapies", "Facial Therapies", "Bespoke Rituals"…), fetched via
// get_chidesk_treatment_categories — because that is how guests browse a spa: by
// type first, then the specific treatment + duration.
//
// AVAILABILITY IS ON-SELECT + LIVE (see state/useChideskAvailability.ts):
//   • Opening a category shows its treatments INSTANTLY from static config — no
//     ChiDesk calls, no per-treatment greying. Browsing costs zero latency.
//   • The moment a guest SELECTS a treatment (with a date set) we fire a LIVE
//     union: one available_times call per bookable therapist, merged as they
//     resolve (dedupe by exact `start`, union free-therapist ids). Times appear
//     progressively. There is NO cache — changing the treatment, the date or the
//     therapist re-fetches fresh (a cached slot could already be booked).
//   • Therapist is OPTIONAL and LAST. Default "Any available" = the union above;
//     picking a chip does ONE live call for that therapist only. The flow reads
//     treatment → time → (optional) therapist.
//
// A slot's `start` is ChiDesk's EXACT fake-Z Start string. It is echoed VERBATIM
// into cart metadata on booking and NEVER passed through new Date() for display
// — all display is label/regex driven. Adding creates one cart item per
// treatment; the chain runs sequentially.

import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { useCart } from "../state/cart";
import { supabase } from "../config";
import { showWhatsMissing } from "../lib/show-whats-missing";
import { optimizedImageUrl, optimizedSrcSet, IMG_WIDTHS } from "../lib/image";
import type { WidgetBrand, WidgetChideskTreatment, WidgetService } from "../types";
import { addMinutes } from "../state/booking";
import {
  checkChideskSlot,
  fetchChideskBookingConfig,
  fetchChideskTreatmentCategories,
  previewChideskDeposit,
  type ChideskDepositPreview,
  type ChideskSlot,
  type ChideskTreatmentCategory,
} from "../state/chidesk";
import {
  deriveFeasibleStarts,
  labelFromWallMinutes,
  normMinute,
  parseClockLabel,
  resolveChain,
  statusOf,
  wallMinutesOf,
  type CardStatus,
  type TimeOfDay,
} from "../state/chidesk-availability";
import { useChideskLiveAvailability } from "../state/useChideskAvailability";
import { hoursPillText, loadServiceHours, type ServiceHoursRow } from "../state/service-hours";
import { setTripDateFromIso, tripDateAsDate, tripDateIsFuture } from "../state/trip-date";
import { DaySelector } from "./chidesk/DaySelector";
import { FilterBar } from "./chidesk/FilterBar";
import { TimeChips } from "./chidesk/TimeChips";
import { WidgetLayer } from "./WidgetLayer";

// Treatments with no ChiDesk category land here — always ordered last.
const OTHER_CATEGORY_KEY = "__other__";
const OTHER_CATEGORY_LABEL = "Other treatments";

export type TreatmentGroup = {
  key: string; // the category label, or OTHER_CATEGORY_KEY
  label: string;
  colour: string | null; // ChiDesk calendar colour (optional flavour)
  items: WidgetChideskTreatment[];
};

// Group the menu by TREATMENT TYPE (ChiDesk Category). The category map is keyed
// by widget treatment id; a treatment with no (or not-yet-loaded) category falls
// into "Other treatments". Groups are ordered by category name, "Other" last.
function groupTreatmentsByCategory(
  treatments: WidgetChideskTreatment[],
  categoryMap: Map<string, ChideskTreatmentCategory>,
): TreatmentGroup[] {
  const buckets = new Map<string, TreatmentGroup>();
  for (const t of treatments) {
    const info = categoryMap.get(t.id);
    const cat = info?.category ?? null;
    const key = cat ?? OTHER_CATEGORY_KEY;
    let bucket = buckets.get(key);
    if (!bucket) {
      bucket = { key, label: cat ?? OTHER_CATEGORY_LABEL, colour: info?.colour ?? null, items: [] };
      buckets.set(key, bucket);
    }
    bucket.items.push(t);
  }
  const named = [...buckets.values()]
    .filter((b) => b.key !== OTHER_CATEGORY_KEY)
    .sort((a, b) => a.label.localeCompare(b.label));
  const other = buckets.get(OTHER_CATEGORY_KEY);
  return other ? [...named, other] : named;
}

// Only render a ChiDesk calendar colour if it's a clean hex — never trust it to
// carry the whole UI; it's a small accent dot on the group header at most.
function cleanHexColour(c: string | null): string | null {
  return c && /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(c) ? c : null;
}

const COLLAPSE_THRESHOLD = 8;

// Local YYYY-MM-DD from a Date — LOCAL parts (no tz hazard).
function ymd(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

// Human day label from a YYYY-MM-DD. Uses LOCAL noon of OUR own date string
// (not a ChiDesk fake-Z slot) — so this new Date() is tz-safe.
function formatDayLabel(day: string): string {
  const d = new Date(`${day}T12:00:00`);
  if (Number.isNaN(d.getTime())) return day;
  return d.toLocaleDateString("en-ZA", { weekday: "short", day: "numeric", month: "short" });
}

export function ChideskPanel({
  service,
  activeBrand,
  heroBrand,
  heroStyle,
  panelStyle,
  onAddedToCart,
}: {
  service: WidgetService;
  activeBrand: WidgetBrand | null;
  heroBrand?: WidgetBrand | null;
  heroStyle?: CSSProperties;
  panelStyle?: CSSProperties;
  onAddedToCart?: () => void;
}) {
  const cart = useCart();
  const chidesk = service.chidesk!;
  const brand = heroBrand ?? activeBrand;
  const heroImage = service.hero_image_url ?? brand?.hero_image_url ?? null;
  const showPrices = service.show_prices_in_widget !== false;

  // Single active treatment. The panel focuses ONE treatment's live
  // availability at a time — picking another switches focus. A combined
  // multi-treatment union was impossible to read (whose time is whose?).
  // Multiple treatments are still booked one at a time; the cart accumulates.
  const [activeId, setActiveId] = useState<string | null>(null);
  // Fixed at 1 until group spa bookings ship (see the WHEN row + the stepper
  // removal). party is still threaded through deposit + booking so re-enabling
  // it later is a one-line change back to a stepper.
  const [party] = useState(1);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [filter, setFilter] = useState("");
  // Which treatment-type groups are expanded. Purely visual now — opening a group
  // no longer triggers any fetch. Seeded once the category map resolves so we
  // open the first real category (or all, if the menu is tiny).
  const [openCategories, setOpenCategories] = useState<Set<string>>(new Set());
  const [categoryMap, setCategoryMap] = useState<Map<string, ChideskTreatmentCategory>>(
    () => new Map(),
  );
  const [categoriesLoaded, setCategoriesLoaded] = useState(false);
  // Yield-management grid + turnaround (empty grid / 0 → no restriction) and
  // the tenant's UTC offset (ChiDesk-UTC → tenant-local; SAST fallback 120).
  const [bookingCfg, setBookingCfg] = useState<{
    bookingGrid: number[];
    turnaroundMinutes: number;
    tzOffsetMinutes: number;
  }>({ bookingGrid: [], turnaroundMinutes: 0, tzOffsetMinutes: 120 });
  const seededOpenRef = useRef(false);

  // Date-confirm: guests pick the TIME here but the DATE lives higher up (the
  // DaySelector) and is easy to overlook. If they never changed it off the
  // seeded day, confirm the date before booking (mirrors ServicePanel's
  // date-confirm). dateTouchedRef arms when they change the day; dateConfirmedRef
  // suppresses re-asking once acknowledged.
  const dateConfirmedRef = useRef(false);
  const dateTouchedRef = useRef(false);
  const [dateConfirm, setDateConfirm] = useState<{ onYes: () => void } | null>(
    null,
  );

  // ── Faceted state ────────────────────────────────────────────────────────
  // day = source of truth (seeded on the guest's trip date if they planned one).
  const [day, setDay] = useState<string>(() => {
    const trip = tripDateAsDate();
    return trip && tripDateIsFuture() ? ymd(trip) : ymd(new Date());
  });
  const [therapistFilter, setTherapistFilter] = useState<string | null>(null); // staff ROW id
  const [tod, setTod] = useState<TimeOfDay>("any");
  const [pickedStart, setPickedStart] = useState<string | null>(null); // EXACT fake-Z anchor
  const [manualStart, setManualStart] = useState<string | null>(null); // "HH:MM" — errored anchor only

  const treatmentsRef = useRef<HTMLDivElement | null>(null);

  // ── Derived selection ───────────────────────────────────────────────────────
  // 0-or-1 treatments — keeps the array shape every downstream memo + the
  // booking path already expect, so "one treatment at a time" needs no chain
  // rewrite: the union hook, feasible starts and resolveChain just operate on
  // a single-element (or empty) chain.
  const selectedChain = useMemo(() => {
    const t = chidesk.treatments.find((x) => x.id === activeId);
    return t ? [t] : [];
  }, [chidesk.treatments, activeId]);
  const therapistFilterGuid = useMemo(
    () =>
      therapistFilter
        ? chidesk.staff.find((s) => s.id === therapistFilter)?.chidesk_id ?? null
        : null,
    [therapistFilter, chidesk.staff],
  );
  // Bookable therapist GUIDs for the "Any available" union — ALWAYS every bookable
  // therapist, NOT gated on the customer picker. `employee_pick_enabled` only
  // controls whether the guest SEES a therapist chooser; it must never shrink the
  // set we compute availability + fair assignment across. Gating it here was a bug:
  // with the picker off the hook fell back to ONE "any" call, which the proxy
  // collapses to the FIRST bookable therapist only — so availability reflected just
  // therapist #1 (empty once they're booked) and every "any" booking piled onto
  // them. `chidesk.staff` is already the is_allowed + is_active set from
  // get_widget_config, independent of the picker flag. Empty staff → the hook's
  // single "any" fallback (unchanged).
  const staffGuids = useMemo(
    () =>
      chidesk.staff.length > 0
        ? chidesk.staff.map((s) => s.chidesk_id).filter((g): g is string => !!g)
        : [],
    [chidesk.staff],
  );

  // ── LIVE availability hook (on-select union, no cache) ──────────────────────
  const { slotsFor, progress, fetching, retry, version } = useChideskLiveAvailability(
    chidesk.integration_id,
    day,
    selectedChain,
    staffGuids,
    therapistFilterGuid,
    bookingCfg.tzOffsetMinutes,
  );

  // ── Derived availability ─────────────────────────────────────────────────────
  const statuses = useMemo(() => {
    void version; // recompute as the live union merges
    const m = new Map<string, CardStatus>();
    for (const t of selectedChain) m.set(t.id, statusOf(slotsFor(t.id)));
    return m;
  }, [selectedChain, slotsFor, version]);
  // 'none' = ChiDesk definitively has no slots that day → drop on add.
  const bookableChain = useMemo(
    () => selectedChain.filter((t) => statuses.get(t.id) !== "none"),
    [selectedChain, statuses],
  );
  const greyed = useMemo(
    () => selectedChain.filter((t) => statuses.get(t.id) === "none"),
    [selectedChain, statuses],
  );
  // Operating hours (loaded via the effect below) → the chosen day's OPEN
  // windows as local wall-clock minute ranges. ChiDesk's availableTimes ignores
  // the spa's opening hours (returns slots a therapist isn't booked, e.g. from
  // 06:00 when the spa opens 09:00), so feasible uses these to cap the chips.
  const [serviceHours, setServiceHours] = useState<ServiceHoursRow[]>([]);
  const openRanges = useMemo(() => {
    const dow = new Date(`${day}T12:00:00`).getDay(); // 0=Sun..6=Sat (tz-safe)
    const toMin = (t: string | null): number | null => {
      const m = t ? /^(\d{2}):(\d{2})/.exec(t) : null;
      return m ? Number(m[1]) * 60 + Number(m[2]) : null;
    };
    const ranges: Array<{ start: number; end: number }> = [];
    for (const r of serviceHours) {
      if (r.day_of_week !== dow || r.is_closed) continue;
      const start = toMin(r.opens_at);
      const end = toMin(r.closes_at);
      if (start != null && end != null && end > start) ranges.push({ start, end });
    }
    return ranges;
  }, [serviceHours, day]);
  // Minute-of-day cutoff for TODAY so already-elapsed slots aren't offered as
  // bookable ("book in the past"). Uses the browser wall clock — consistent
  // with how this panel derives `today` (ymd(new Date())) and with ChiDesk's
  // local fake-Z slot minuteOfDay. Undefined on any future day (no cutoff).
  const nowMinute = useMemo(() => {
    const now = new Date();
    return day === ymd(now)
      ? now.getHours() * 60 + now.getMinutes()
      : undefined;
  }, [day, version]);
  const feasible = useMemo(
    () =>
      deriveFeasibleStarts(bookableChain, slotsFor, {
        filterGuid: therapistFilterGuid,
        tod,
        openRanges,
        nowMinute,
        bookingGrid: bookingCfg.bookingGrid,
        turnaround: bookingCfg.turnaroundMinutes,
      }),
    [
      bookableChain,
      slotsFor,
      therapistFilterGuid,
      tod,
      openRanges,
      nowMinute,
      bookingCfg,
      version,
    ],
  );
  // Staff row ids free at some anchor slot — others dim in the picker. Only
  // meaningful for the union ("Any"): a specific pick fetched ONLY that therapist,
  // so we must not dim the rest (they were never queried).
  const therapistEnabledIds = useMemo(() => {
    void version;
    if (therapistFilter != null) return null; // specific pick → keep every chip live
    if (fetching) return null; // mid live-fetch → can't assert unavailability; keep all tappable
    const anchor = bookableChain[0];
    if (!anchor) return null;
    const ds = slotsFor(anchor.id);
    if (!ds || ds === "loading" || !ds.ok) return null; // unknown → don't disable
    const guids = new Set<string>();
    for (const s of ds.slots) for (const g of s.employeeIds) guids.add(g);
    const ids = new Set<string>();
    for (const st of chidesk.staff) if (guids.has(st.chidesk_id)) ids.add(st.id);
    return ids;
  }, [therapistFilter, fetching, bookableChain, slotsFor, chidesk.staff, version]);

  const dateLabel = formatDayLabel(day);
  const therapistName = therapistFilter
    ? chidesk.staff.find((s) => s.id === therapistFilter)?.name ?? "this therapist"
    : null;
  // A specific therapist with a definitively-empty day is a SOFT "no times with X"
  // (offer Any/next-day) — never the generic "No availability".
  // Anchor the "no times with this therapist" hint on the BOOKABLE chain (fall
  // back to the selected chain only when nothing is bookable) — else a dropped
  // first treatment suppresses a bookable sibling's live time chips.
  const anchorForHint = bookableChain[0] ?? selectedChain[0] ?? null;
  const anchorSlots = anchorForHint ? slotsFor(anchorForHint.id) : undefined;
  const softTherapistEmpty =
    therapistFilterGuid != null &&
    anchorSlots != null &&
    anchorSlots !== "loading" &&
    anchorSlots.ok &&
    anchorSlots.slots.length === 0;

  // All type groups (unfiltered) — the source for seeding + grouping.
  const allGroups = useMemo(
    () => groupTreatmentsByCategory(chidesk.treatments, categoryMap),
    [chidesk.treatments, categoryMap],
  );
  const filtering = filter.trim().length > 0;
  // Search narrows within groups and drops emptied ones — but keeps grouping.
  const treatmentGroups = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return allGroups;
    return allGroups
      .map((g) => ({ ...g, items: g.items.filter((t) => t.name.toLowerCase().includes(q)) }))
      .filter((g) => g.items.length > 0);
  }, [allGroups, filter]);

  // Whether a group renders open: an active search force-opens every matching
  // group; otherwise it's the guest's toggle, or a group holding a picked item.
  const isGroupOpen = useCallback(
    (g: TreatmentGroup) =>
      filtering || openCategories.has(g.key) || g.items.some((t) => t.id === activeId),
    [filtering, openCategories, activeId],
  );

  // The inline booking details (time chips, therapist, deposit, CTA) render INSIDE
  // the expanded category card that holds the guest's selection — right where
  // they're looking. If a search hides that group, bookingHostKey falls to null
  // and the details render once as a fallback below the grid.
  const bookingHostKey = useMemo(() => {
    for (const g of treatmentGroups) {
      if (g.items.some((t) => t.id === activeId)) return g.key;
    }
    return null;
  }, [treatmentGroups, activeId]);

  function toggleCategory(key: string) {
    setOpenCategories((prev) => {
      if (prev.has(key)) {
        const next = new Set(prev);
        next.delete(key);
        return next;
      }
      // Opening a category collapses the OTHERS that have nothing selected —
      // only the one being opened stays in the set. The category holding the
      // active treatment stays open on its own (isGroupOpen keeps it open via
      // the activeId clause), so we don't need to add it here.
      return new Set([key]);
    });
  }

  function toggleTreatment(id: string) {
    // Single-select: focus this treatment, or unfocus if it's already active.
    // Switching clears the pinned time — it belonged to the previous
    // treatment's live slots and means nothing for the new one.
    setActiveId((prev) => (prev === id ? null : id));
    setPickedStart(null);
    setManualStart(null);
  }

  // ── Deposit preview ──────────────────────────────────────────────────────────
  const [depositPreview, setDepositPreview] = useState<ChideskDepositPreview | null>(null);
  useEffect(() => {
    if (selectedChain.length === 0) {
      setDepositPreview(null);
      return;
    }
    let cancelled = false;
    (async () => {
      const result = await previewChideskDeposit(
        selectedChain.map((t) => ({ treatment_row_id: t.id, qty: party })),
      );
      if (cancelled) return;
      setDepositPreview(result.ok ? result : null);
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedChain, party]);

  // ── Operating hours load (state declared above, near feasible) ──────────────
  useEffect(() => {
    let cancelled = false;
    loadServiceHours(service.id).then((rows) => {
      if (!cancelled) setServiceHours(rows);
    });
    return () => {
      cancelled = true;
    };
  }, [service.id]);
  const pickedDate = useMemo(() => new Date(`${day}T12:00:00`), [day]);
  const hoursPill = useMemo(() => hoursPillText(serviceHours, pickedDate), [serviceHours, pickedDate]);

  // ── Booking horizon (cap the date picker) ───────────────────────────────────
  const [horizonDays, setHorizonDays] = useState<number | null>(null);
  useEffect(() => {
    let alive = true;
    supabase
      .rpc("get_service_booking_window", { p_service_id: service.id })
      .then(({ data }: { data: unknown }) => {
        const d = (data as { max_advance_days?: number | null } | null)?.max_advance_days;
        if (alive) setHorizonDays(typeof d === "number" && d > 0 ? d : null);
      });
    return () => {
      alive = false;
    };
  }, [service.id]);
  const todayYmd = useMemo(() => ymd(new Date()), []);
  const dayMax = useMemo(() => {
    if (horizonDays == null) return undefined;
    const d = new Date();
    d.setDate(d.getDate() + horizonDays);
    return ymd(d);
  }, [horizonDays]);

  // ── Category map: fetch once when the integration id is known ───────────────
  // This is a Supabase RPC for grouping labels — NOT a ChiDesk availability call.
  useEffect(() => {
    let alive = true;
    fetchChideskTreatmentCategories(chidesk.integration_id).then((m) => {
      if (!alive) return;
      setCategoryMap(m);
      setCategoriesLoaded(true);
    });
    return () => {
      alive = false;
    };
  }, [chidesk.integration_id]);

  // Yield-management config (allowed times grid + turnaround).
  useEffect(() => {
    let alive = true;
    fetchChideskBookingConfig(chidesk.integration_id).then((cfg) => {
      if (alive) setBookingCfg(cfg);
    });
    return () => {
      alive = false;
    };
  }, [chidesk.integration_id]);

  // Seed the open groups ONCE the categories resolve: open all for a tiny menu,
  // else just the first type so the guest expands the rest on demand.
  useEffect(() => {
    if (!categoriesLoaded || seededOpenRef.current || allGroups.length === 0) return;
    seededOpenRef.current = true;
    setOpenCategories(
      chidesk.treatments.length <= COLLAPSE_THRESHOLD
        ? new Set(allGroups.map((g) => g.key))
        : new Set(allGroups.slice(0, 1).map((g) => g.key)),
    );
  }, [categoriesLoaded, allGroups, chidesk.treatments.length]);

  // Drop a pickedStart that left the live chips (day/therapist/treatment change).
  useEffect(() => {
    if (
      pickedStart &&
      feasible.status === "ready" &&
      !feasible.chips.some((c) => c.start === pickedStart)
    ) {
      setPickedStart(null);
    }
  }, [feasible, pickedStart]);

  // Day change resets the picked/manual time.
  useEffect(() => {
    setPickedStart(null);
    setManualStart(null);
  }, [day]);

  // ── Totals (party-scaled) ──────────────────────────────────────────────────
  const totalCents = selectedChain.reduce((n, t) => n + (t.price_cents ?? 0), 0);
  const partyTotalCents = totalCents * party;
  const currency = selectedChain[0]?.currency ?? "ZAR";
  const depositPct =
    depositPreview && depositPreview.total_cents > 0 && partyTotalCents > 0
      ? Math.round((depositPreview.total_cents / partyTotalCents) * 100)
      : null;

  const hasTime = pickedStart != null || manualStart != null;

  function changeDay(next: string) {
    dateTouchedRef.current = true; // guest explicitly chose a day → don't confirm
    setDay(next);
    setTripDateFromIso(`${next}T12:00:00`); // local noon — tz-safe
  }
  function goNextDay() {
    const d = new Date(`${day}T12:00:00`);
    d.setDate(d.getDate() + 1);
    const next = ymd(d);
    if (dayMax && next > dayMax) return;
    changeDay(next);
  }

  async function onAdd() {
    if (selectedChain.length === 0) {
      setError("Pick at least one treatment.");
      return;
    }
    // Every selected treatment is definitively unavailable today.
    if (greyed.length > 0 && bookableChain.length === 0) {
      setError(`Not available on ${dateLabel}.`);
      return;
    }
    if (bookableChain.length === 0) {
      setError("Pick at least one available treatment.");
      return;
    }
    if (!hasTime) {
      setError("Pick a time.");
      return;
    }
    // The guest picked a treatment + time, but the DATE lives higher up and is
    // easy to forget. If they never changed it off the seeded day, confirm the
    // date before booking. onYes re-runs onAdd with the flag set, so it books.
    if (!dateConfirmedRef.current && !dateTouchedRef.current) {
      setDateConfirm({
        onYes: () => {
          dateConfirmedRef.current = true;
          setDateConfirm(null);
          void onAdd();
        },
      });
      return;
    }
    setError(null);
    setFeedback(null);
    setPending(true);

    const anchor = bookableChain[0]!;
    let firstSlot: ChideskSlot | null = null;
    if (pickedStart) {
      const ds = slotsFor(anchor.id);
      if (ds && ds !== "loading" && ds.ok) {
        firstSlot = ds.slots.find((s) => s.start === pickedStart) ?? null;
      }
      if (!firstSlot) {
        // Pinned start not in the anchor's slots (e.g. anchor errored) — trust the
        // pinned start verbatim. pickedStart is the slot's UTC string; the
        // displayed minute/label are tenant-local (+offset), matching byMinute.
        const localMin = normMinute(wallMinutesOf(pickedStart) + bookingCfg.tzOffsetMinutes);
        firstSlot = {
          start: pickedStart,
          label: labelFromWallMinutes(localMin),
          minuteOfDay: localMin,
          employeeIds: therapistFilterGuid ? [therapistFilterGuid] : [],
        };
      }
    } else if (manualStart) {
      // The typed time is TENANT-LOCAL; the booking start string must be the
      // corresponding UTC instant (local − offset), with day rollover handled.
      const min = parseClockLabel(manualStart) ?? 0;
      const [y, mo, d] = day.split("-").map(Number);
      const utcMs = Date.UTC(y!, mo! - 1, d!, 0, min - bookingCfg.tzOffsetMinutes, 0);
      const iso = new Date(utcMs).toISOString().replace(/\.\d{3}Z$/, "Z");
      firstSlot = {
        start: iso,
        label: labelFromWallMinutes(min),
        minuteOfDay: min,
        employeeIds: therapistFilterGuid ? [therapistFilterGuid] : [],
      };
    }
    if (!firstSlot) {
      setError("Pick a time.");
      setPending(false);
      return;
    }

    const legs = resolveChain(bookableChain, slotsFor, firstSlot, therapistFilterGuid);
    if (!legs) {
      setError("That combination isn’t available at the chosen time — pick another time.");
      setPending(false);
      return;
    }

    // ── Final LIVE guard ──────────────────────────────────────────────────────
    // The union was fetched when the treatment was selected and could have gone
    // stale before the guest booked. Re-check the anchor's EXACT start now against
    // the SAME therapist we're about to book (legs[0].staffGuid — a free therapist
    // from the slot's union). Checking staffGuid=null would resolve to the proxy's
    // first-bookable therapist and false-negative any union slot owned by someone
    // else. FAIL OPEN: an unknown/transport result proceeds (the chidesk-proxy
    // create-time re-check is the real backstop) — only a DEFINITIVE "gone" blocks.
    // Skip when there's no concrete therapist (manual/synthesized start: the slot
    // is intentionally unverified) so we can't false-negative a real slot.
    const guardStaff = legs[0]!.staffGuid;
    if (pickedStart && guardStaff) {
      const guard = await checkChideskSlot({
        tenantIntegrationId: chidesk.integration_id,
        treatmentGuid: legs[0]!.treatment.chidesk_id,
        staffGuid: guardStaff,
        startsAt: legs[0]!.start,
        tzOffsetMinutes: bookingCfg.tzOffsetMinutes,
      });
      if (guard.ok && guard.available === false) {
        setError(`That ${legs[0]!.label} slot was just taken — pick another time.`);
        setPickedStart(null);
        setPending(false);
        retry(); // refetch live so the chips update
        return;
      }
    }

    // Deposit must be authoritative. depositPreview is fetched async on selection
    // change; if the guest books before it resolves it's still null and the
    // per-line deposit_cents would freeze at 0. Resolve it now.
    let preview = depositPreview;
    if (!preview) {
      const res = await previewChideskDeposit(
        bookableChain.map((t) => ({ treatment_row_id: t.id, qty: party })),
      );
      preview = res.ok ? res : null;
    }

    for (const leg of legs) {
      const t = leg.treatment;
      const startsAt = leg.start; // VERBATIM fake-Z — never reformatted
      const endsAt = addMinutes(leg.start, leg.durationMin); // Z-space arithmetic
      // Only name a therapist when the guest SPECIFICALLY picked one. For "any"
      // the server assigns (fairly) at booking time, so the cart line + deposit
      // stay therapist-agnostic and the confirmation shows who was assigned.
      const staffRow =
        therapistFilterGuid && leg.staffGuid
          ? chidesk.staff.find((s) => s.chidesk_id === leg.staffGuid) ?? null
          : null;
      // Cart line time range — BOTH ends in the same 24h format the cart uses
      // elsewhere (the when-chip + humanWindow show "16:30", "16:30→16:45").
      // Was leg.label (ChiDesk's raw "16:30 PM") + a 12h endLabel, which mixed
      // formats and read as if the end came before the start
      // ("16:30 PM – 04:45 PM").
      const fmt24 = (min: number) =>
        `${String(Math.floor(min / 60)).padStart(2, "0")}:${String(min % 60).padStart(2, "0")}`;
      const startLabel = fmt24(leg.minuteOfDay);
      const endLabel = fmt24(leg.minuteOfDay + leg.durationMin);
      // Candidates for server-side FAIR assignment (least-loaded). For "Any
      // available" this is every therapist free at the slot; for a specific
      // pick it's just that one. The proxy circulates among these instead of
      // always booking the first, and resolves the assigned therapist's name
      // for the confirmation.
      const staffCandidates = leg.employeeIds.map((g) => {
        const row = chidesk.staff.find((s) => s.chidesk_id === g) ?? null;
        return { guid: g, staff_row_id: row?.id ?? null, name: row?.name ?? null };
      });

      cart.add({
        serviceId: service.id,
        serviceName: service.name,
        serviceType: service.service_type,
        unitId: null,
        unitName: null,
        description: describeByLabel(t, staffRow?.name ?? null, dateLabel, startLabel, endLabel),
        quantity: party,
        unitPrice: t.price_cents != null ? t.price_cents / 100 : null,
        currency: t.currency ?? currency,
        brandId: activeBrand?.id ?? null,
        brandName: activeBrand?.name ?? null,
        metadata: {
          source: "chidesk",
          chidesk_integration_id: chidesk.integration_id,
          chidesk_treatment_id: t.id,
          chidesk_treatment_guid: t.chidesk_id,
          chidesk_staff_id: staffRow?.id ?? null,
          chidesk_staff_guid: therapistFilterGuid ? leg.staffGuid : null,
          chidesk_staff_candidates: staffCandidates,
          starts_at: startsAt,
          ends_at: endsAt,
          amount_cents: t.price_cents ?? 0,
          deposit_cents:
            preview?.items.find((d) => d.treatment_row_id === t.id)?.deposit_cents_total ?? 0,
          duration_minutes: leg.durationMin,
          treatment_name: t.name,
          requires_full_payment: false,
          // Tenant UTC-offset so cart displays convert the UTC starts_at/ends_at
          // to tenant-local wall clock (CartPanel formatChipDateTime/humanWindow).
          tz_offset_minutes: bookingCfg.tzOffsetMinutes,
        },
      });
    }
    // Pin the trip date to the chosen day with a tz-safe local-noon stamp.
    setTripDateFromIso(`${day}T12:00:00`);

    onAddedToCart?.();
    setFeedback(`Added ${legs[0]?.treatment.name ?? "treatment"}.`);
    setActiveId(null);
    setPickedStart(null);
    setManualStart(null);
    setPending(false);
    window.setTimeout(() => setFeedback(null), 2500);
  }

  const heroCtaLabel = feedback
    ? "Added ✓"
    : pending
      ? "Adding…"
      : selectedChain.length === 0
        ? "Choose a treatment ↓"
        : !hasTime
          ? "Pick a time ↓"
          : "Add treatment →";

  // A press on Book before a time is picked: take the guest to the times
  // (lib/show-whats-missing.ts). Never a dead button.
  const bookingRef = useRef<HTMLDivElement | null>(null);
  function showTimes() {
    const booking = bookingRef.current;
    const chips = booking?.querySelector<HTMLElement>(".vt-time-chips") ?? booking;
    showWhatsMissing(chips, chips?.querySelector<HTMLElement>("button:not(:disabled)"));
  }

  // The INLINE booking details — a prominent LIVE-checking banner, therapist
  // choice, available time chips, deposit line and the Book CTA — rendered inside
  // the expanded category card holding the selection (or as a fallback below).
  const bookingPanel =
    selectedChain.length > 0 ? (
      <div className="vt-chidesk-booking" ref={bookingRef}>
        {fetching && (
          <div className="vt-chidesk-checking" role="status" aria-live="polite">
            {/* Brand-coloured fill grows with progress (done/total) to VISUALISE
                the live-availability load. It sits behind the text (which is
                charcoal for contrast on both the filled + unfilled portions). */}
            <span
              className="vt-chidesk-checking-fill"
              aria-hidden="true"
              style={{
                width: progress
                  ? `${Math.round((progress.done / Math.max(progress.total, 1)) * 100)}%`
                  : "0%",
              }}
            />
            <span className="vt-chidesk-checking-dot" aria-hidden="true" />
            <span>
              Checking live availability…
              {progress && (
                <span className="vt-chidesk-checking-sub">
                  {" "}
                  checking therapists — {progress.done} of {progress.total}
                </span>
              )}
            </span>
          </div>
        )}
        <FilterBar
          staff={chidesk.staff}
          employeePickEnabled={chidesk.employee_pick_enabled}
          therapistFilter={therapistFilter}
          onTherapistChange={setTherapistFilter}
          therapistEnabledIds={therapistEnabledIds}
          tod={tod}
          onTodChange={setTod}
        />
        {softTherapistEmpty ? (
          <p className="vt-chidesk-window-hint">
            No times with {therapistName} on {dateLabel} — try “Any available” or another day.
          </p>
        ) : (
          <TimeChips
            feasible={feasible}
            pickedStart={pickedStart}
            onPick={setPickedStart}
            dateLabel={dateLabel}
            onRetry={retry}
            manualStart={manualStart}
            onManualStart={setManualStart}
            onNextDay={goNextDay}
          />
        )}

        {depositPreview && depositPreview.total_cents > 0 && (
          <div className="vt-chidesk-deposit-note">
            Deposit due at checkout:{" "}
            <strong>{formatMoney(depositPreview.total_cents, depositPreview.currency)}</strong>
            {depositPct !== null && depositPct > 0 && depositPct < 100 && (
              <> ({depositPct}% deposit)</>
            )}{" "}
            — balance is settled at the venue.
          </div>
        )}

        {error && <div className="vt-chidesk-error">{error}</div>}

        <div className="vt-action-row">
          <div className="vt-action-status">
            {selectedChain[0]
              ? showPrices
                ? `${selectedChain[0].name}${party > 1 ? ` × ${party}` : ""} · ${formatMoney(partyTotalCents, currency)}`
                : `${selectedChain[0].name}${party > 1 ? ` × ${party} guests` : ""}`
              : "Choose a treatment"}
          </div>
          <button
            type="button"
            className={`vt-cta-inline${
              feedback ? " vt-cta-inline-success" : !hasTime && !pending ? " vt-cta-blocked" : ""
            }`}
            onClick={hasTime ? onAdd : showTimes}
            disabled={pending}
            aria-disabled={(!hasTime && !pending && !feedback) || undefined}
          >
            {feedback ? "Added ✓" : pending ? "Adding…" : hasTime ? "Add treatment →" : "Pick a time ↑"}
          </button>
        </div>
      </div>
    ) : null;

  return (
    <div className="vt-panel" style={panelStyle}>
      {dateConfirm && (
        <WidgetLayer>
          <div
            onClick={() => setDateConfirm(null)}
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 2147483647,
              display: "grid",
              placeItems: "center",
              background: "rgba(0,0,0,0.45)",
              padding: 16,
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                background: "#fff",
                borderRadius: 14,
                padding: "20px 22px",
                maxWidth: 400,
                width: "100%",
                boxShadow: "0 12px 44px rgba(0,0,0,.24)",
              }}
            >
              <div style={{ fontWeight: 700, fontSize: 16, color: "#1A1A1A" }}>
                Confirm your date &amp; time
              </div>
              <p
                style={{
                  marginTop: 8,
                  fontSize: 14,
                  lineHeight: 1.5,
                  color: "#4A4A4A",
                }}
              >
                You haven&rsquo;t changed the date, so this books for{" "}
                <strong>
                  {dateLabel}
                  {pickedStart
                    ? ` at ${labelFromWallMinutes(normMinute(wallMinutesOf(pickedStart) + bookingCfg.tzOffsetMinutes))}`
                    : ""}
                </strong>. Is that right?
              </p>
              <button
                type="button"
                onClick={dateConfirm.onYes}
                style={{
                  display: "block",
                  width: "100%",
                  marginTop: 16,
                  padding: "12px 16px",
                  borderRadius: 10,
                  border: "none",
                  background: "var(--vt-accent, #5B3DF5)",
                  color: "#fff",
                  fontSize: 15,
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                Yes, book for {dateLabel}
              </button>
              <button
                type="button"
                onClick={() => setDateConfirm(null)}
                style={{
                  display: "block",
                  width: "100%",
                  marginTop: 8,
                  padding: "8px",
                  border: "none",
                  background: "transparent",
                  color: "#6A6A6A",
                  fontSize: 14,
                  cursor: "pointer",
                }}
              >
                Change date/time
              </button>
            </div>
          </div>
        </WidgetLayer>
      )}
      <div className="vt-service-card">
        <div className="vt-hero" style={heroStyle}>
          {heroImage ? (
            <img
              src={optimizedImageUrl(heroImage, { width: 1280 })}
              srcSet={optimizedSrcSet(heroImage, IMG_WIDTHS.HERO)}
              sizes="100vw"
              alt={service.name}
              decoding="async"
            />
          ) : (
            <div className={`vt-hero-gradient vt-hero-${service.service_type}`} />
          )}
          <div className="vt-hero-overlay">
            <div className="vt-hero-overlay-text">
              <h2>{service.name}</h2>
              <p className="vt-hero-tag">{service.tagline || "Choose a treatment to see live times"}</p>
            </div>
            <div className="vt-hero-pills">
              <div
                className={`vt-hours-pill${hoursPill?.isOpen === false ? " vt-hours-pill-closed" : ""}`}
                role="status"
              >
                <span className="vt-hours-pill-dot" aria-hidden="true" />
                {hoursPill?.label ?? "Open today — book any available slot"}
              </div>
              <button
                type="button"
                className={`vt-cta-inline vt-hero-cta${feedback ? " vt-cta-inline-success" : ""}`}
                onClick={() => {
                  if (selectedChain.length === 0) {
                    treatmentsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
                    return;
                  }
                  if (!hasTime) {
                    showTimes();
                    return;
                  }
                  void onAdd();
                }}
                disabled={pending}
              >
                {heroCtaLabel}
              </button>
            </div>
          </div>
        </div>

        <div className="vt-service-body">
          {service.description && <p className="vt-panel-description">{service.description}</p>}

          {/* WHEN row: date picker + treatment filter side by side to save
              vertical space. The party stepper is intentionally hidden for now —
              spa bookings are capped at 1 guest until group bookings ship. */}
          <div className="vt-chidesk-when-row" ref={treatmentsRef}>
            <DaySelector day={day} min={todayYmd} max={dayMax} onChange={changeDay} />
            {chidesk.treatments.length > COLLAPSE_THRESHOLD && (
              <div className="vt-field vt-chidesk-filter-field">
                <label>Filter treatments</label>
                <input
                  type="search"
                  className="vt-chidesk-filter"
                  placeholder="Search treatments…"
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                />
              </div>
            )}
          </div>

          {/* Choose treatments label row */}
          <div className="vt-section-label-row">
            <h4 className="vt-section-label-title">Choose a treatment</h4>
            <span className="vt-section-label-hint">One at a time · add more after</span>
          </div>

          {chidesk.treatments.length === 0 && (
            <p className="vt-chidesk-empty">No treatments available right now.</p>
          )}
          {chidesk.treatments.length > 0 && !categoriesLoaded && (
            <p className="vt-chidesk-window-hint" role="status">
              Loading treatments…
            </p>
          )}
          {chidesk.treatments.length > 0 && categoriesLoaded && treatmentGroups.length === 0 && (
            <p className="vt-chidesk-empty">No treatments match “{filter.trim()}”.</p>
          )}

          {categoriesLoaded && (
            <div className="vt-chidesk-catgrid">
              {treatmentGroups.map((group) => {
                const isOpen = isGroupOpen(group);
                const activeInGroup = group.items.some((t) => t.id === activeId);
                const dot = cleanHexColour(group.colour);
                return (
                  <div
                    key={group.key}
                    className={`vt-chidesk-catcard${isOpen ? " vt-chidesk-catcard-open" : ""}`}
                  >
                    <button
                      type="button"
                      className="vt-chidesk-catcard-header"
                      aria-expanded={isOpen}
                      onClick={() => toggleCategory(group.key)}
                    >
                      <span className="vt-chidesk-band-title">
                        {dot && (
                          <span
                            aria-hidden="true"
                            style={{
                              display: "inline-block",
                              width: 8,
                              height: 8,
                              borderRadius: "50%",
                              background: dot,
                              marginRight: 8,
                              verticalAlign: "middle",
                            }}
                          />
                        )}
                        {group.label}
                      </span>
                      <span className="vt-chidesk-band-count">
                        {activeInGroup && (
                          <span className="vt-chidesk-band-selected">selected · </span>
                        )}
                        {!isOpen && group.items.length}
                        {/* When open, an explicit "Close" pill so it's obvious
                            the category collapses — a bare chevron read as
                            decoration. */}
                        <span
                          className={`vt-chidesk-band-chevron${isOpen ? " vt-chidesk-band-chevron-open" : ""}`}
                          aria-hidden="true"
                        >
                          {isOpen ? "Close ▲" : "▾"}
                        </span>
                      </span>
                    </button>
                    {isOpen && (
                      <div className="vt-chidesk-catcard-body">
                        <ul className="vt-chidesk-treatments">
                          {group.items.map((t) => {
                            const checked = t.id === activeId;
                            const inCartLines = cart.items.filter(
                              (i) =>
                                i.serviceId === service.id &&
                                i.metadata?.source === "chidesk" &&
                                i.metadata?.chidesk_treatment_id === t.id,
                            );
                            const inCart = inCartLines.length > 0;
                            const durationLabel = t.duration_minutes
                              ? `${t.duration_minutes} min`
                              : "Duration varies";
                            const priceLabel =
                              showPrices && t.price_cents != null
                                ? formatMoney(t.price_cents, t.currency ?? currency)
                                : null;
                            return (
                              <li
                                key={t.id}
                                className={`vt-chidesk-treatment${
                                  checked ? " vt-chidesk-treatment-selected" : ""
                                }`}
                              >
                                <label className="vt-chidesk-treatment-main">
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() => toggleTreatment(t.id)}
                                  />
                                  <div className="vt-chidesk-treatment-body">
                                    <div className="vt-chidesk-treatment-title">{t.name}</div>
                                    <div className="vt-chidesk-treatment-meta">
                                      {durationLabel}
                                      {priceLabel && <> · {priceLabel}</>}
                                    </div>
                                  </div>
                                </label>
                                {inCart && (
                                  <div className="vt-chidesk-treatment-foot">
                                    <div className="vt-option-incart" role="status">
                                      In booking ✓
                                    </div>
                                    <div className="vt-chidesk-treatment-foot-actions">
                                      <button
                                        type="button"
                                        className="vt-option-card-book vt-option-card-book-remove"
                                        onClick={() =>
                                          inCartLines.forEach((line) => cart.remove(line.id))
                                        }
                                      >
                                        Remove
                                      </button>
                                    </div>
                                  </div>
                                )}
                              </li>
                            );
                          })}
                        </ul>
                        {group.key === bookingHostKey && bookingPanel}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Fallback: if a search hid the group holding the selection, the inline
              booking details render once here so booking is never stranded. */}
          {selectedChain.length > 0 && bookingHostKey == null && bookingPanel}
        </div>
      </div>
    </div>
  );
}

function describeByLabel(
  treatment: WidgetChideskTreatment,
  staffName: string | null,
  dayLabel: string,
  startLabel: string,
  endLabel: string,
): string {
  const with_ = staffName ? ` with ${staffName}` : "";
  return `${treatment.name}${with_} · ${dayLabel} · ${startLabel} – ${endLabel}`;
}

function formatMoney(cents: number, currency: string): string {
  const symbol = currency === "ZAR" ? "R" : currency;
  const whole = Math.round(cents / 100);
  return `${symbol} ${whole.toLocaleString("en-ZA")}`;
}
